import { useEffect, useRef, useState } from 'react';

import { Button, Form } from 'react-bootstrap';

import { PendingApproval } from './PendingApproval';
import { StepLog } from './StepLog';

// 500ms is fast enough to read the proposed step before it auto-approves,
// but tight enough that long runs don't accumulate dead time on the QA's
// side. Was 1500 — too much padding on 50-step tests.
const AUTO_APPROVE_DELAY_MS = 500;

/**
 * Active-run sidebar contents (progress accordion, auto-approve toggle,
 * pending approval, status). Owns the auto-approve timer.
 *
 * Returns sidebar fragments only — Runner.js owns the grid layout and the
 * Viewport so the iframe stays mounted across the running → summary transition.
 */
export const StepRun = ({
    autoApprove,
    currentPhaseIndex,
    currentStepIndex,
    hd,
    onApprove,
    onCancel,
    onDisableHD,
    onEnableHD,
    onSkip,
    phases,
    setAutoApprove,
    state
}) => {

    const { pending, status, steps } = state;

    const [showDetails, setShowDetails] = useState(false);

    const autoApproveRef = useRef(autoApprove);
    const onApproveRef = useRef(onApprove);

    useEffect(() => {

        autoApproveRef.current = autoApprove;

    }, [autoApprove]);
    useEffect(() => {

        onApproveRef.current = onApprove;

    }, [onApprove]);

    useEffect(() => {

        if (!pending || !autoApprove) return;

        const handle = setTimeout(() => {

            if (autoApproveRef.current && onApproveRef.current)
                onApproveRef.current(null);

        }, AUTO_APPROVE_DELAY_MS);

        return () => clearTimeout(handle);

    }, [pending, autoApprove]);

    return (

        <>

            <div className="d-flex justify-content-between align-items-baseline">
                <h6 className="text-uppercase text-muted m-0" style={{ fontSize: 11, letterSpacing: 0.5 }}>
                    Progress
                </h6>
                <button
                    className="btn btn-link btn-sm p-0 text-info text-decoration-none"
                    onClick={() => setShowDetails(v => !v)}
                    style={{ fontSize: 11 }}
                    type="button"
                >
                    {showDetails ? 'Hide details' : 'Show details'}
                </button>
            </div>

            <StepLog
                currentPhaseIndex={currentPhaseIndex}
                currentStepIndex={currentStepIndex}
                phases={phases}
                showDetails={showDetails}
                status={status}
                steps={steps}
            />

            <Form.Check
                checked={autoApprove}
                className="text-light small"
                id="run-auto-approve"
                label="Auto-approve next step"
                onChange={e => setAutoApprove(e.target.checked)}
                type="switch"
            />

            {hd && (
                <div className="small">
                    {hd.enabled ? (
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            <span className="text-info">
                                ● HD capture on
                                <span className="text-muted ms-1">
                                    (
                                    {hd.mode === 'region' ? 'region-cropped' : 'tab-cropped'}
                                    )
                                </span>
                            </span>
                            <button
                                className="btn btn-link btn-sm p-0 text-decoration-none"
                                onClick={onDisableHD}
                                style={{ fontSize: 11 }}
                                type="button"
                            >
                                turn off
                            </button>
                        </div>
                    ) : (
                        <div>
                            <Button
                                disabled={hd.busy || hd.supported === false}
                                onClick={onEnableHD}
                                size="sm"
                                variant="outline-info"
                            >
                                {hd.busy ? 'Requesting share…' : 'Enable HD screenshots'}
                            </Button>
                            {hd.error && (
                                <div className="text-danger mt-1" style={{ fontSize: 11 }}>
                                    {hd.error}
                                </div>
                            )}
                            {hd.supported === false && (
                                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                    Tab-share isn&apos;t available in this browser.
                                </div>
                            )}
                            {hd.supported !== false && !hd.error && (
                                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                    Real browser pixels for screenshot / capture_evidence. Falls back to html2canvas if you decline.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {(status === 'running' || status === 'connected') && (
                <Button
                    onClick={onCancel}
                    size="sm"
                    variant="outline-danger"
                >
                    Stop run
                </Button>
            )}

            <PendingApproval
                autoApprove={autoApprove}
                onApprove={onApprove}
                onCancel={onCancel}
                onSkip={onSkip}
                pending={pending}
            />

            <div className="text-muted small mt-auto">
                {status}
            </div>

        </>

    );

};
